import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  CreditCard, Home, History, User, Settings, Shield, Bell,
  CheckCircle, Copy, Eye, EyeOff, Smartphone, QrCode,
  ArrowUpCircle, ArrowDownCircle, Globe, Wallet
} from "lucide-react";

export default function PlatiPomiruMain() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    let parsed = null;
    
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {}
    }
    
    if (!parsed) {
      try {
        const stored = localStorage.getItem('platipomiru_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) setData(parsed);
  }, [searchParams]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-slate-500 text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная', active: true },
    { icon: CreditCard, label: 'Моя карта' },
    { icon: History, label: 'История' },
    { icon: Wallet, label: 'Пополнить' },
    { icon: User, label: 'Профиль' },
    { icon: Settings, label: 'Настройки' },
  ];

  const recentTransactions = (data.transactions || []).slice(0, 5);

  return (
    <div className="min-h-screen bg-[#f8f9fb] template-preview-scale" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-slate-800 text-lg">Плати по миру</span>
                <p className="text-xs text-slate-500">Virtual Cards</p>
              </div>
            </div>

            {/* User Section */}
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-slate-400 hover:text-slate-600">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
              </button>
              
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-violet-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {data.firstName?.[0]}{data.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-slate-800">{data.fullName}</p>
                  <p className="text-xs text-slate-500">ID: {data.userId}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <nav className="space-y-1">
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={index}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                        item.active 
                          ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-md' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Support Card */}
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-violet-50 rounded-xl">
                <p className="text-sm font-medium text-slate-800 mb-1">Поддержка 24/7</p>
                <p className="text-xs text-slate-500 mb-3">Мы всегда на связи в Telegram</p>
                <button className="w-full py-2 bg-white text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors">
                  Написать в поддержку
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-6">
            {/* Card Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Ваша карта</h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-700 text-sm font-medium">Активна</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Virtual Card */}
                <div className="relative">
                  <div className="aspect-[1.586/1] bg-gradient-to-br from-blue-500 via-violet-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl overflow-hidden">
                    {/* Card Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    </div>
                    
                    <div className="relative h-full flex flex-col justify-between">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white/70 text-xs mb-1">{data.card.type}</p>
                          <p className="text-2xl font-bold">{data.card.balance} {data.card.currency === 'USD' ? '$' : '€'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Globe className="w-6 h-6 text-white/80" />
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-lg tracking-widest font-mono mb-4">
                          {showCardDetails ? data.card.number : '•••• •••• •••• ' + data.card.number.slice(-4)}
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white/60 text-xs">ВЛАДЕЛЕЦ</p>
                            <p className="font-medium">{data.fullName.toUpperCase()}</p>
                          </div>
                          <div>
                            <p className="text-white/60 text-xs">СРОК</p>
                            <p className="font-medium">{data.card.expiry}</p>
                          </div>
                          <div>
                            <p className="text-white/60 text-xs">CVV</p>
                            <p className="font-medium">{showCardDetails ? data.card.cvv : '•••'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    <button 
                      onClick={() => setShowCardDetails(!showCardDetails)}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                    >
                      {showCardDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showCardDetails ? 'Скрыть' : 'Показать'}
                    </button>
                    <button 
                      onClick={() => copyToClipboard(data.card.number.replace(/\s/g, ''), 'number')}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      {copied === 'number' ? 'Скопировано!' : 'Копировать номер'}
                    </button>
                  </div>
                </div>

                {/* Card Info */}
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-500 text-sm">Номер карты</span>
                      <button onClick={() => copyToClipboard(data.card.number.replace(/\s/g, ''), 'num2')}>
                        <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </button>
                    </div>
                    <p className="font-mono text-slate-800">{showCardDetails ? data.card.number : '•••• •••• •••• ' + data.card.number.slice(-4)}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <span className="text-slate-500 text-sm">Срок действия</span>
                      <p className="font-medium text-slate-800 mt-1">{data.card.expiry}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <span className="text-slate-500 text-sm">CVV</span>
                      <p className="font-medium text-slate-800 mt-1">{showCardDetails ? data.card.cvv : '•••'}</p>
                    </div>
                  </div>

                  {/* Pay Methods */}
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-violet-50 rounded-xl">
                    <Smartphone className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">Apple Pay & Google Pay</p>
                      <p className="text-xs text-slate-500">Подключены и готовы к оплате</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button className="p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group">
                <ArrowUpCircle className="w-8 h-8 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">Пополнить</p>
                <p className="text-xs text-slate-500">Через СБП</p>
              </button>
              <button className="p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group">
                <ArrowDownCircle className="w-8 h-8 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">Перевести</p>
                <p className="text-xs text-slate-500">На карту</p>
              </button>
              <button className="p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group">
                <QrCode className="w-8 h-8 text-violet-500 mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">QR-код</p>
                <p className="text-xs text-slate-500">Для оплаты</p>
              </button>
              <button className="p-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group">
                <Shield className="w-8 h-8 text-amber-500 mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">3D Secure</p>
                <p className="text-xs text-slate-500">Настроить</p>
              </button>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800">Последние операции</h3>
                <button className="text-blue-600 text-sm font-medium hover:underline">Все операции →</button>
              </div>
              
              <div className="space-y-3">
                {recentTransactions.map((tx, index) => (
                  <div key={index} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                        tx.type === 'topup' ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        {tx.type === 'topup' ? '💳' : tx.icon}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{tx.description}</p>
                        {data.showSenders !== false && tx.sender && (
                          <p className="text-xs text-slate-500">Отправитель: {tx.sender}</p>
                        )}
                        <p className="text-xs text-slate-400">{tx.date}</p>
                      </div>
                    </div>
                    <div className={`font-semibold ${tx.type === 'topup' ? 'text-green-600' : tx.type === 'withdrawal' ? 'text-orange-600' : 'text-slate-700'}`}>
                      {tx.type === 'topup' ? `+${tx.amountRub?.toLocaleString()} ₽` : tx.type === 'withdrawal' ? `-${tx.amountRub?.toLocaleString()} ₽` : `-${tx.amount} ${tx.currency}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
